// pages/ChatSpace/ChatSpaceCitations.js
import React from 'react';
import {
    Box,
    Typography,
    IconButton,
    Link,
    Chip,
    Divider,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { marked } from 'marked';

const CitationItem = ({ citation }) => {
    const getHtml = (content) => {
        try {
            const extractSummary = (text) => {
                const match = text.match(/<source_page_summary>([\s\S]*?)<\/source_page_summary>/);
                return match ? match[1].trim() : citation.summary;
            };
            const contentToParse = extractSummary(content || citation.summary);
            return marked.parse(contentToParse || 'No summary available.');
        } catch {
            return citation.summary || 'No summary available.';
        }
    };

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.5,
                mb: 1.5,
                bgcolor: 'grey.50',
                '&:hover': { boxShadow: 1 },
                transition: 'box-shadow 0.2s',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Link
                    href={citation.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: '0.75rem', fontWeight: 600, flex: 1, lineHeight: 1.3 }}
                    underline="hover"
                >
                    {citation.title_tag || 'Source Document'}
                </Link>
                <OpenInNewIcon sx={{ fontSize: 14, color: 'grey.400', flexShrink: 0, mt: 0.25 }} />
            </Box>
            <Chip
                label={(citation.data_source || '').replace('_', ' ')}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20, mb: 1 }}
            />
            <Divider sx={{ mb: 1 }} />
            <Box
                sx={{
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    '& p': { m: 0 },
                    '& a': { color: 'primary.main' },
                    overflow: 'hidden',
                    maxHeight: 120,
                }}
                dangerouslySetInnerHTML={{ __html: getHtml(citation.page_content) }}
            />
        </Box>
    );
};

const CollapsedCitationItem = ({ citation, index }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
        <IconButton
            size="small"
            onClick={() => window.open(citation.url, '_blank', 'noopener,noreferrer')}
            title={citation.title_tag}
            sx={{ width: 36, height: 36 }}
        >
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'primary.light',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                }}
            >
                {index + 1}
            </Box>
        </IconButton>
    </Box>
);

export default function ChatSpaceCitations({ isOpen, isCollapsed = false, onToggle, onClose, citations = [] }) {
    if (!isOpen) return null;

    return (
        <>
            {/* Mobile overlay */}
            <Box
                onClick={onClose}
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    position: 'fixed',
                    inset: 0,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    zIndex: 40,
                }}
            />

            <Box
                sx={{
                    position: { xs: 'fixed', lg: 'relative' },
                    top: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: { xs: 50, lg: 'auto' },
                    width: { xs: 280, lg: '100%' },
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    bgcolor: { xs: 'background.paper', lg: 'transparent' },
                    boxShadow: { xs: 8, lg: 0 },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isCollapsed ? 'center' : 'space-between',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        px: isCollapsed ? 1 : 2,
                        py: 1.5,
                        flexShrink: 0,
                    }}
                >
                    {!isCollapsed ? (
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700}>Sources</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {citations.length} source{citations.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center' }}>
                            <MenuBookIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Typography variant="caption" display="block" color="text.secondary">
                                {citations.length}
                            </Typography>
                        </Box>
                    )}
                    {!isCollapsed && onToggle && (
                        <IconButton size="small" onClick={onToggle} title="Collapse">
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto', p: isCollapsed ? 0.5 : 1.5 }}>
                    {citations.length > 0 ? (
                        isCollapsed
                            ? citations.map((c, i) => <CollapsedCitationItem key={i} citation={c} index={i} />)
                            : citations.map((c, i) => <CitationItem key={i} citation={c} />)
                    ) : (
                        !isCollapsed && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <OpenInNewIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">No sources available</Typography>
                            </Box>
                        )
                    )}
                </Box>
            </Box>
        </>
    );
}
